import * as React from 'react';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Info } from 'lucide-react';
import { useSettingsState } from '../../../components/context/SettingsContext';
import { Button } from '@/components/ui/button';
import { InfoTooltipCard } from './InfoTooltipCard';

export function SpecialistInfoCard({
  imageUrl,
  name,
  specialty,
  address,
  services,
  defaultServiceId,
  infoItems,
  specialistId,
  serviceTitle,
  specialistUrl,
  notes,
  complex = false,
  onSelect,
  sgk,
}) {
  const { getSpecialistImage, getDefaultSpecialistImage } = useSettingsState();

  return (
    <Card className="w-full max-w-4xl border-none rounded-none shadow-none font-primary min-w-0">
      {' '}
      {/* ✅ added min-w-0 */}
      <CardHeader className="flex flex-col sm:flex-row sm:space-x-4 w-full sm:items-start min-w-0">
        {' '}
        {/* ✅ added min-w-0 */}
        <div className="flex justify-center sm:block shrink-0">
          <Avatar className="w-24 h-24 sm:w-36 sm:h-36 shrink-0">
            <AvatarImage
              src={`${process.env.REACT_APP_API_BASE}/Specialists/${specialistId}/picture`}
              className="w-full h-full rounded-full object-cover"
            />
            <AvatarFallback>
              <img
                src={getDefaultSpecialistImage()}
                alt="Default avatar"
                className="w-full h-full rounded-full object-cover"
              />
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="w-full flex flex-col mt-4 min-w-0">
          {' '}
          {/* ✅ added break-words + min-w-0 */}
          <Badge
            variant="outline"
            className="inline-block whitespace-normal text-[#6C6C7D] border-[#6C6C7D] rounded-sm w-fit"
          >
            {specialty}
          </Badge>
          <div className="flex items-center space-x-2 font-semibold text-[#1F4B81] text-xl min-w-0">
            {specialistUrl && (
              <Info
                className="w-5 h-5 shrink-0 cursor-pointer"
                onClick={() => window.open(specialistUrl, '_blank')}
              />
            )}
            <span className="min-w-0">{name}</span>
          </div>
          <div className="text-base text-[#6C6C7D] font-normal min-w-0">
            {serviceTitle}
          </div>
          {complex && (
            <Button
              className="!w-full bg-[#F9FCFF] text-[#1F4B81]"
              onClick={onSelect}
            >
              Rinktis
            </Button>
          )}
          <div className="text-base text-[#6C6C7D] font-normal min-w-0">
            {address}
          </div>
        </div>
      </CardHeader>
      <Separator />
      <CardFooter className="flex-col items-stretch gap-4">
        <InfoGroup>
          {infoItems?.map((item, index) => (
            <InfoItem
              key={index}
              label={item.label}
              value={item.value}
              align={item.align || 'left'}
              sgk={sgk}
            />
          ))}
        </InfoGroup>
        {!!notes?.trim() && <SpecialistNotes notes={notes} />}
      </CardFooter>
    </Card>
  );
}

const OptionSelector = ({ services, defaultValue }) => {
  return (
    <div className="space-y-2">
      <div className="text-base font-semibold text-[#6C6C7D] uppercase text-left">
        Paslaugos
      </div>
      <RadioGroup defaultValue={defaultValue}>
        {services?.map((service) => (
          <div className="flex items-center gap-3" key={service.id}>
            <RadioGroupItem value={service.id} id={service.id} />
            <Label
              htmlFor={service.id}
              className="text-sm font-normal text-gray-800"
            >
              {service.label}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
};

const InfoGroup = ({ children }) => {
  return (
    <div className="flex flex-row justify-between w-full gap-4">{children}</div>
  );
};

const SpecialistNotes = ({ notes }) => {
  const lines = notes
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    <div className="w-full border-t pt-4 text-left">
      <div className="text-sm text-[#6C6C7D] font-normal mb-1">Pastabos</div>
      {lines.length > 1 ? (
        <ul
          className="space-y-1 text-sm leading-5"
          style={{ listStyleType: 'disc', paddingLeft: '1.25rem' }}
        >
          {lines.map((line, index) => (
            <li key={`${index}-${line}`}>{line}</li>
          ))}
        </ul>
      ) : (
        <div className="text-sm leading-5">{lines[0]}</div>
      )}
    </div>
  );
};

const InfoItem = ({ label, value, align = 'left', sgk }) => {
  const { agreements } = useSettingsState();
  const alignmentClass = {
    left: 'text-left items-start',
    center: 'text-center items-center',
    right: 'text-right items-end',
  }[align];

  return (
    <div className={`flex flex-col ${alignmentClass}`}>
      <span className="text-sm text-[#6C6C7D] font-normal flex items-center gap-1">
        {label}
        {value === 'Taip' && sgk && (
          <InfoTooltipCard>
            Paslauga kompensuojama PSDF lėšomis tik prisirašiusiems pacientams
            ir tik pasirinkus "PSDF kompensuojamas laikas"
          </InfoTooltipCard>
        )}
        {value === 'Taip' && !sgk && (
          <InfoTooltipCard>
            Iš PSDF biudžeto kompensuojamos paslaugos teikiamos pacientams
            nemokamai tik tuo atveju, jeigu pacientas pateikia gydytojo išduotą
            siuntimą ir yra visos teisės aktuose nurodytos prielaidos (sąlygos),
            kurioms esant atitinkamos paslaugos gali būti teikiamos pacientams
            nemokamai. Daugiau informacijos:{' '}
            <a
              href={agreements.clinicRules.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-800"
            >
              čia
            </a>
          </InfoTooltipCard>
        )}
      </span>
      <span className="text-base font-normal">{value}</span>
    </div>
  );
};
